'use client'

import React from 'react'
import classNames from 'classnames'
import { Header } from '@/ui/components/Header'
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical'
import { StandardCard } from '@/ui/components/Card/StandardCard'
import { Media } from '@/payload-types'
import { LabelProps } from '@/ui/atoms/Label'
import { AllowedFavouriteCollection } from '@/utils/favourites/types'
import { Icon } from '@/ui/atoms/Icon'

export type PlatformCardData = {
  title: string
  description?: string
  image?: Media | string | null
  imageTag?: string
  date?: Date | string | null
  minutesToRead?: number | null
  labels?: Array<{
    title: string
    variant?: LabelProps['variant']
  }>
  category?: {
    title: string
    colorScheme: string
  }
  company?: {
    name: string
    logo?: Media | string
  }
  authors?: Array<{
    name: string
    avatar?: Media | string | null
    verified?: boolean
  }>
  appearance?: 'filled' | 'outlined'
  isExternal?: boolean
  collection?: AllowedFavouriteCollection | null
  itemId?: string
  // Features card fields
  showFeatures?: boolean
  icon?: string
  cardTitle?: string
  cardDescription?: string
  features?: string[]
}

interface PlatformPreviewProps {
  label?: string
  title?: string
  description?: SerializedEditorState
  cards: PlatformCardData[]
  className?: string
}

export const PlatformPreview: React.FC<PlatformPreviewProps> = ({
  label,
  title,
  description,
  cards,
  className,
}) => {
  return (
    <div className={classNames('w-full', className)}>
      {/* Header Section */}
      <Header label={label} title={title} description={description} />

      {/* Cards Grid - 2 columns on lg */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {cards.map((card, index) => {
          // Render Features Card
          if (card.showFeatures) {
            return (
              <div
                key={index}
                className="relative overflow-hidden bg-bg-subtle border border-border-subtle rounded-lg p-8 lg:p-10 flex items-center justify-center min-h-[400px]"
              >
                {/* Decorative gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-97/30 to-transparent pointer-events-none" />

                <div className="relative z-10 flex flex-col items-center justify-center w-full">
                  {/* Header with Icon */}
                  <div className="flex items-start gap-4 mb-8 max-w-md w-full">
                    {card.icon && (
                      <div className="flex-shrink-0 w-12 h-12 rounded-md bg-soft-gradient-blue flex items-center justify-center">
                        <Icon name={card.icon} className="!size-6 text-white" />
                      </div>
                    )}
                    <div className="flex-1">
                      {card.cardTitle && (
                        <h3 className="text-headline-M-semibold text-text-base mb-2">
                          {card.cardTitle}
                        </h3>
                      )}
                      {card.cardDescription && (
                        <p className="text-body-M-regular text-text-subtle">
                          {card.cardDescription}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Features List */}
                  {card.features && card.features.length > 0 && (
                    <ul className="space-y-3.5 max-w-md w-full">
                      {card.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <span className="flex-shrink-0 rounded-sm bg-soft-gradient-blue size-5 flex items-center justify-center mt-1">
                            <Icon name="ri-check-line" className="!size-3 text-white" />
                          </span>
                          <span className="text-body-M-regular text-text-base flex-1">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )
          }

          // Render Standard Card
          return <StandardCard key={index} {...card} />
        })}
      </div>
    </div>
  )
}
